import React, { useMemo, useState } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Box,
  Typography,
} from "@mui/material";
import { KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { TableLoadingSkeleton } from "src/shared/components/tables/table-loading-skeleton";

export type ITreeNode = {
  id: string | number;
  code: string;
  name: string;
  node_type?: string;
  children?: ITreeNode[];
  opening_balance: {
    credit: number;
    debit: number;
  };
  movements: {
    credit: number;
    debit: number;
  };
};

type Props = {
  data: any[];
  isLoading: boolean;
};

// Show the first two decimals only, WITHOUT rounding (e.g. 100931.06634 -> "100931.06").
// We format the raw number at its stored precision (decimal(16,8)) and slice the decimals,
// so no rounding is ever applied to what the user sees.
const truncateTo2 = (value: number): string => {
  const safe = Number.isFinite(value) ? value : 0;
  const sign = safe < 0 ? "-" : "";
  const [intPart, decPart = ""] = Math.abs(safe).toFixed(8).split(".");
  return `${sign}${intPart}.${`${decPart}00`.slice(0, 2)}`;
};

type NodeValues = {
  openingDebit: number;
  openingCredit: number;
  movementDebit: number;
  movementCredit: number;
  netMovementDebit: number;
  netMovementCredit: number;
  closingDebit: number;
  closingCredit: number;
};

// Derive the displayed figures for a single account node. Parent (high-level) nodes already
// aggregate their descendants, so this is used both for rendering and for the grand totals.
const computeNodeValues = (node: ITreeNode): NodeValues => {
  const openingDebit = node.opening_balance?.debit ?? 0;
  const openingCredit = node.opening_balance?.credit ?? 0;
  const movementDebit = node.movements?.debit ?? 0;
  const movementCredit = node.movements?.credit ?? 0;

  const netMovementDebit = Math.max(0, movementDebit - movementCredit);
  const netMovementCredit = Math.max(0, movementCredit - movementDebit);

  const closingDebit = Math.max(
    0,
    openingDebit - openingCredit + (netMovementDebit - netMovementCredit),
  );
  const closingCredit = Math.max(
    0,
    openingCredit - openingDebit + (netMovementCredit - netMovementDebit),
  );

  return {
    openingDebit,
    openingCredit,
    movementDebit,
    movementCredit,
    netMovementDebit,
    netMovementCredit,
    closingDebit,
    closingCredit,
  };
};

const headerCellSx = {
  fontSize: "12px",
  textTransform: "uppercase" as const,
  color: "#fff",
  lineHeight: "18px",
  fontWeight: 500,
};

const TrialBalanceTable: React.FC<Props> = ({ data, isLoading }) => {
  const { t: generalT } = useTranslation("general");

  const [expanded, setExpanded] = useState<Record<string | number, boolean>>(
    {},
  );

  const toggle = (id: string | number) => {
    setExpanded((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // Totals must sum only the top-level (high-level) accounts, never their nested children —
  // a parent already aggregates its descendants, so expanding a node must not change the totals.
  const totals = useMemo(() => {
    return (data ?? []).reduce<NodeValues>(
      (acc, node) => {
        const v = computeNodeValues(node);
        acc.openingDebit += v.openingDebit;
        acc.openingCredit += v.openingCredit;
        acc.movementDebit += v.movementDebit;
        acc.movementCredit += v.movementCredit;
        acc.netMovementDebit += v.netMovementDebit;
        acc.netMovementCredit += v.netMovementCredit;
        acc.closingDebit += v.closingDebit;
        acc.closingCredit += v.closingCredit;
        return acc;
      },
      {
        openingDebit: 0,
        openingCredit: 0,
        movementDebit: 0,
        movementCredit: 0,
        netMovementDebit: 0,
        netMovementCredit: 0,
        closingDebit: 0,
        closingCredit: 0,
      },
    );
  }, [data]);

  const renderRows = (nodes: ITreeNode[], depth = 0): React.ReactNode[] => {
    return nodes.flatMap((node) => {
      const isOpen = expanded[node.id];
      const hasChildren = node.children && node.children?.length > 0;

      const {
        openingDebit,
        openingCredit,
        movementDebit,
        movementCredit,
        netMovementDebit,
        netMovementCredit,
        closingDebit,
        closingCredit,
      } = computeNodeValues(node);

      return [
        <TableRow
          key={node.id}
          hover
          sx={{
            bgcolor:
              node.node_type === "category" ? "primary.light" : "transparent",
          }}
        >
          <TableCell width={80}>{node.code}</TableCell>

          <TableCell size="small">
            <Box display="flex" alignItems="center" pl={depth * 3}>
              {hasChildren && (
                <IconButton size="small" onClick={() => toggle(node.id)}>
                  {isOpen ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                </IconButton>
              )}
              {!hasChildren && <Box width={32} />}
              {node.name}
            </Box>
          </TableCell>

          <TableCell>{truncateTo2(openingDebit)}</TableCell>
          <TableCell>{truncateTo2(openingCredit)}</TableCell>
          <TableCell>{truncateTo2(movementDebit)}</TableCell>
          <TableCell>{truncateTo2(movementCredit)}</TableCell>
          <TableCell>{truncateTo2(netMovementDebit)}</TableCell>
          <TableCell>{truncateTo2(netMovementCredit)}</TableCell>
          <TableCell>{truncateTo2(closingDebit)}</TableCell>
          <TableCell>{truncateTo2(closingCredit)}</TableCell>
        </TableRow>,

        ...(hasChildren && isOpen ? renderRows(node.children!, depth + 1) : []),
      ];
    });
  };

  const groupHeaders = [
    { label: generalT("code"), colSpan: 1, rowSpan: 2 },
    { label: generalT("accountName"), colSpan: 1, rowSpan: 2 },
    { label: generalT("openingBalance"), colSpan: 2 },
    { label: generalT("movement"), colSpan: 2 },
    { label: generalT("netMovement"), colSpan: 2 },
    { label: generalT("closingBalance"), colSpan: 2 },
  ];

  return (
    <Table>
      <TableHead>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {groupHeaders.map((header) => (
            <TableCell
              key={header.label}
              colSpan={header.colSpan}
              rowSpan={header.rowSpan}
              align="center"
              sx={{
                borderRight: "1px solid rgba(255,255,255,0.2)",
                "&:last-child": { borderRight: "none" },
              }}
            >
              <Typography {...headerCellSx}>{header.label}</Typography>
            </TableCell>
          ))}
        </TableRow>
        <TableRow sx={{ bgcolor: "primary.main" }}>
          {Array.from({ length: 4 }).map((_, i) => (
            <React.Fragment key={i}>
              <TableCell align="center">
                <Typography {...headerCellSx}>
                  {generalT("debit")}
                </Typography>
              </TableCell>
              <TableCell
                align="center"
                sx={{
                  borderRight:
                    i < 3 ? "1px solid rgba(255,255,255,0.2)" : "none",
                }}
              >
                <Typography {...headerCellSx}>
                  {generalT("credit")}
                </Typography>
              </TableCell>
            </React.Fragment>
          ))}
        </TableRow>
      </TableHead>

      {isLoading ? (
        <TableLoadingSkeleton rowsLength={5} cellsLength={10} />
      ) : (
        <TableBody>
          {renderRows(data)}
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>{generalT("total")}</TableCell>
            <TableCell />
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.openingDebit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.openingCredit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.movementDebit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.movementCredit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.netMovementDebit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.netMovementCredit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.closingDebit)}
            </TableCell>
            <TableCell sx={{ fontWeight: 600 }}>
              {truncateTo2(totals.closingCredit)}
            </TableCell>
          </TableRow>
        </TableBody>
      )}
    </Table>
  );
};

export default TrialBalanceTable;
